<script lang="ts">
	//Design system components
	import {
		Button,
		ButtonGroup,
		ControlsContainer,
		Modal,
		Text,
		Container,
		toast,
	} from '@Shelly/shelly-design-system';
	//Design system helpers
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { capFirstChar } from '../../../helpers/utilities';
	//Services
	import { format } from 'svelte-i18n';
	//Helpers
	import { real_id } from 'src/helpers/interface_helpers';
	import { changeLightMode, resolveLightColorType } from 'src/helpers/light_mode_change';
	//Stores
	import {
		createMemberSettingsSnapshot,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Types
	import type { Device } from '../../../types/Device';
	import type { LightMode } from 'src/types/LightType';

	export let change_mode_lights: Device[] = [];
	export let hasRGBW2 = false;
	export let blocking_type: string | null = null;

	const modes: LightMode[] = ['color', 'white'];

	let showModal = false;
	let pendingMode: LightMode | null = null;
	let loading = false;

	$: memberIds = change_mode_lights?.map((device) => device.id) ?? [];
	$: memberSettingsStore = createMemberSettingsSnapshot(memberIds);
	$: memberSettings = $memberSettingsStore;
	$: firstDevice = change_mode_lights?.[0];
	$: firstSettings = firstDevice ? memberSettings[real_id(firstDevice.id)] : undefined;
	$: colorType = firstDevice
		? resolveLightColorType(firstDevice, firstSettings, firstDevice.mode)
		: 'color';

	const dispatchModeChange = async (newMode: LightMode) => {
		loading = true;
		const results = await Promise.all(
			change_mode_lights.map((device) => {
				const settings = readDeviceSettings(device.id);
				return changeLightMode(device, settings, newMode);
			}),
		);
		const failed = results.filter((r) => !r?.isok).length;
		if (failed > 0) {
			toast.open({
				type: 'danger',
				title: $format('error_global'),
			});
		}
		loading = false;
	};

	const onModeClick = async (newMode: LightMode) => {
		if (newMode === colorType) return;
		if (hasRGBW2) {
			pendingMode = newMode;
			showModal = true;
			return;
		}
		await dispatchModeChange(newMode);
	};

	const confirmModal = async () => {
		showModal = false;
		if (pendingMode) {
			const next = pendingMode;
			pendingMode = null;
			await dispatchModeChange(next);
		}
	};
</script>

<ControlsContainer class="mb-xl" color={$showNewLayout ? 'color-0' : undefined}>
	<svelte:fragment slot="first">
		<ButtonGroup
			id="buttongroup-ifpkiv6"
			aria-label={$format('device_modes', { default: 'Device Modes' })}
		>
			{#each modes as _mode}
				<Button
					class={classMap({
						active: colorType === _mode,
					})}
					size="m"
					loading={loading && colorType !== _mode}
					on:click={async () => await onModeClick(_mode)}
				>
					{capFirstChar($format(`mode_${_mode}`))}
				</Button>
			{/each}
		</ButtonGroup>
	</svelte:fragment>
</ControlsContainer>

{#if showModal}
	<Modal
		{showModal}
		on:close={() => {
			showModal = false;
			pendingMode = null;
		}}
		title={$format('label_info')}
	>
		<svelte:fragment slot="content">
			<Container padding="0">
				<Text>
					{#if blocking_type}
						{$format('group_mode_unsupported_desc', {
							default:
								"Mode change isn't supported from groups when a {blockingType} device is included.",
							values: { blockingType: blocking_type },
						})}
					{:else}
						{$format('rgbw_group_mode_desc', {
							default: "RGBW2 changing mode isn't supported from the groups.",
						})}
					{/if}
				</Text>
			</Container>
		</svelte:fragment>
		<div slot="footer" class="d-flex justify-content-end">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => {
					showModal = false;
					pendingMode = null;
				}}
			>
				{capFirstChar($format('button_cancel'))}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{
					sm: 'm',
				}}
				variant={'primary'}
				on:click={confirmModal}
			>
				{capFirstChar($format('button_next'))}
			</Button>
		</div>
	</Modal>
{/if}
